import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar, Box, Drawer, IconButton, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Toolbar, Typography, Button, Chip,
  ToggleButtonGroup, ToggleButton, Badge, Popover, Divider
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, Apartment, People,
  Receipt, AttachMoney, Category, Assessment, AdminPanelSettings, History,
  Notifications as NotificationsIcon, DoneAll, MeetingRoom, AccountBalance, WaterDrop, HelpOutline
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getNotifications, markAllNotificationsRead, markNotificationRead, USE_MOCK_DATA } from '../services/api';

const drawerWidth = 240;

const superAdminMenuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/' },
  { text: 'All Apartments', icon: <Apartment />, path: '/apartments' },
  { text: 'Categories', icon: <Category />, path: '/categories' },
  { text: 'Reports', icon: <Assessment />, path: '/reports' },
  { text: 'Activity Log', icon: <History />, path: '/activity' },
  { text: 'Profile', icon: <People />, path: '/profile' },
  { text: 'Help', icon: <HelpOutline />, path: '/help' },
];

const normalMenuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/' },
  { text: 'Expenses', icon: <Receipt />, path: '/expenses' },
  { text: 'Contributions', icon: <AttachMoney />, path: '/contributions' },
  { text: 'Corpus Fund', icon: <AccountBalance />, path: '/corpus-fund' },
  { text: 'Function Hall', icon: <MeetingRoom />, path: '/function-hall' },
  { text: 'Reports', icon: <Assessment />, path: '/reports' },
  { text: 'Activity Log', icon: <History />, path: '/activity' },
  { text: 'Profile', icon: <People />, path: '/profile' },
  { text: 'Help', icon: <HelpOutline />, path: '/help' },
];

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userApartmentId, setUserApartmentId] = useState(null);
  const { user, logout, switchRole, debugMode } = useAuth();
  const navigate = useNavigate();

  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Mock notifications for debug mode
  const mockNotifications = [
    { id: 1, title: 'New Expense Added', message: 'Rahul Sharma added: Lift repair - ₹15,000', type: 'Expense', isRead: false, createdAt: '2025-05-05T10:30:00' },
    { id: 2, title: 'New Expense Added', message: 'Rahul Sharma added: EB bill May - ₹12,000', type: 'Expense', isRead: false, createdAt: '2025-05-10T11:00:00' },
    { id: 3, title: 'New Expense Added', message: 'Vikram Singh added: Water tanker - ₹5,000', type: 'Expense', isRead: true, createdAt: '2025-05-12T14:20:00' },
  ];

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setNotifications(mockNotifications);
      return;
    }
    getNotifications().then(res => setNotifications(res.data)).catch(() => {});
    // Fetch user's apartment for dynamic menu
    getApartments().then(res => {
      if (res.data && res.data.length > 0) {
        setUserApartmentId(res.data[0].id);
      }
    }).catch(() => {});
  }, []);

  const handleMarkAllRead = async () => {
    if (USE_MOCK_DATA) {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      return;
    }
    await markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.isRead) {
      if (USE_MOCK_DATA) {
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
      } else {
        await markNotificationRead(notif.id);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
      }
    }
  };

  const getMenuItems = () => {
    const adminMenuItems = [
      { text: 'Dashboard', icon: <Dashboard />, path: '/' },
      { text: 'My Apartment', icon: <Apartment />, path: '/apartments' },
      { text: 'Members', icon: <People />, path: `/apartments/${userApartmentId || 1}/users` },
      { text: 'Expenses', icon: <Receipt />, path: '/expenses' },
      { text: 'Water Split Bill', icon: <WaterDrop />, path: '/water-bill' },
      { text: 'Flat Wise Bill', icon: <Receipt />, path: '/flat-bills' },
      { text: 'Contributions', icon: <AttachMoney />, path: '/contributions' },
      { text: 'Corpus Fund', icon: <AccountBalance />, path: '/corpus-fund' },
      { text: 'Festivals', icon: <MeetingRoom />, path: '/festivals' },
      { text: 'Function Hall', icon: <MeetingRoom />, path: '/function-hall' },
      { text: 'Reports', icon: <Assessment />, path: '/reports' },
      { text: 'Activity Log', icon: <History />, path: '/activity' },
      { text: 'Profile', icon: <People />, path: '/profile' },
      { text: 'Help', icon: <HelpOutline />, path: '/help' },
    ];

    switch (user?.roleName) {
      case 'SuperAdmin': return superAdminMenuItems;
      case 'Admin': return adminMenuItems;
      default: return normalMenuItems;
    }
  };

  const getRoleColor = () => {
    switch (user?.roleName) {
      case 'SuperAdmin': return 'error';
      case 'Admin': return 'warning';
      default: return 'default';
    }
  };

  const menuItems = getMenuItems();

  const drawer = (
    <Box>
      <Toolbar />
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="h6" noWrap sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>AEM</Typography>
        <Chip
          icon={<AdminPanelSettings sx={{ fontSize: 16 }} />}
          label={user?.roleName || 'User'}
          size="small"
          color={getRoleColor()}
          sx={{ mt: 0.5 }}
        />
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton onClick={() => { navigate(item.path); setMobileOpen(false); }}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontSize: { xs: '0.9rem', sm: '1.25rem' }, cursor: 'pointer' }} onClick={() => navigate('/')}>
            Apartment Expense Manager
          </Typography>
          <Typography variant="body2" sx={{ mr: 1, cursor: 'pointer', display: { xs: 'none', md: 'block' }, '&:hover': { textDecoration: 'underline' } }} onClick={() => navigate('/profile')}>
            {user?.fullName || 'Guest'} ({user?.roleName || 'N/A'})
          </Typography>
          <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ mr: 1 }}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ width: 350, maxHeight: 400, overflow: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderBottom: '1px solid #eee' }}>
                <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
                {unreadCount > 0 && (
                  <Button size="small" startIcon={<DoneAll />} onClick={handleMarkAllRead}>Mark all read</Button>
                )}
              </Box>
              {notifications.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No notifications</Typography>
              ) : (
                notifications.map(notif => (
                  <Box
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    sx={{ p: 1.5, borderBottom: '1px solid #f5f5f5', bgcolor: notif.isRead ? 'transparent' : '#e3f2fd', cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    <Typography variant="body2" fontWeight={notif.isRead ? 400 : 600}>{notif.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{notif.message}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {new Date(notif.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Popover>
          {debugMode && (
            <ToggleButtonGroup
              value={user?.roleName}
              exclusive
              onChange={(_, val) => { if (val) { switchRole(val); navigate('/'); } }}
              size="small"
              sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.15)', '& .MuiToggleButton-root': { color: 'white', borderColor: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', py: 0.3, px: 1 }, '& .Mui-selected': { bgcolor: 'rgba(255,255,255,0.25) !important' } }}
            >
              <ToggleButton value="SuperAdmin">Super</ToggleButton>
              <ToggleButton value="Admin">Admin</ToggleButton>
              <ToggleButton value="Normal">User</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button color="inherit" onClick={logout}>Logout</Button>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth } }} open>
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 1.5, sm: 3 }, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
